import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Users } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_89039fd3-cb90-48f9-88ab-9d6547dca827/artifacts/ang7mnbh_d04b0c90-39ed-4f51-93a8-9960dfd45601.jpg';

const values = [
  {
    icon: Award,
    title: 'Quality First',
    description: "We use only premium materials that feel good and last longer than one season. Our shirts are built for real life, not just photo ops.",
  },
  {
    icon: Heart,
    title: 'Southern Pride',
    description: "Every design celebrates the heritage and traditions of Southern outdoor culture. We're proud of where we come from and it shows in everything we create.",
  },
  {
    icon: Users,
    title: 'Community',
    description: "Wild Ones isn't just a brand—it's a tribe. We're building a community of like-minded folks who share our love for the outdoors and the Southern way.",
  },
];

export const OurStory = () => {
  return (
    <section className="py-16 md:py-24 bg-wild-sand" data-testid="our-story-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-headings text-4xl md:text-5xl tracking-wider text-wild-dark mb-4">
            OUR STORY
          </h2>
          <p className="font-subheadings text-lg text-wild-stone italic">
            Born in the heart of South Carolina, Wild Ones is more than a brand—it's a way of life.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-16">
          {/* Logo/Image Side */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="w-64 h-64 md:w-80 md:h-80 bg-wild-khaki/30 flex items-center justify-center">
                <img
                  src={LOGO_URL}
                  alt="Wild Ones Bloodhound Logo"
                  className="w-48 h-48 md:w-64 md:h-64 object-contain"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-64 h-64 md:w-80 md:h-80 border-2 border-wild-leather -z-10" />
            </div>
          </div>

          {/* Text Content */}
          <div className="flex flex-col justify-center">
            <h3 className="font-headings text-2xl md:text-3xl tracking-wider text-wild-dark mb-6">
              THE WILD ONES WAY
            </h3>
            <div className="space-y-4 font-body text-wild-stone leading-relaxed">
              <p>
                Every early morning in the duck blind, every sunset on the deer stand, every cast into still waters—these are the moments that define us. Wild Ones was created for people who understand that the best memories are made outdoors, with mud on your boots and a story to tell.
              </p>
              <p>
                We started this brand because we couldn't find apparel that truly represented the Southern outdoor lifestyle. Sure, there were hunting brands and fishing brands, but nothing that captured the spirit—the pride, the tradition, the wild freedom of living close to the land.
              </p>
              <p>
                Every piece we design tells a story. Our bloodhound represents the loyalty and tracking instincts that every hunter admires. Our mallard in flight captures that heart-pounding moment when the ducks come in low. These aren't just shirts—they're conversation starters, tribe identifiers, badges of a life well-lived.
              </p>
              <p className="font-subheadings text-lg text-wild-leather italic pt-2">
                Welcome to the pack. Once a Wild One, always a Wild One.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h3 className="font-headings text-2xl md:text-3xl tracking-wider text-wild-dark text-center mb-10">
            WHAT WE STAND FOR
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white border border-wild-khaki/50 hover:border-wild-leather/50 transition-colors duration-300"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 flex items-center justify-center bg-wild-leather/10 mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-wild-leather" />
                </div>
                <h4 className="font-headings text-xl tracking-wider text-wild-dark mb-3">
                  {value.title}
                </h4>
                <p className="font-body text-wild-stone text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center py-12 bg-wild-khaki/30">
          <h3 className="font-headings text-2xl md:text-3xl tracking-wider text-wild-dark mb-4">
            READY TO RUN WILD?
          </h3>
          <p className="font-body text-wild-stone mb-8">
            Join the pack and represent the Wild Ones lifestyle.
          </p>
          <Link to="/shop">
            <Button
              className="bg-wild-leather text-white hover:bg-wild-leather/90 px-10 py-6 text-lg font-headings tracking-widest"
              data-testid="story-shop-now-btn"
            >
              SHOP NOW
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
